document.addEventListener('DOMContentLoaded', () => {
  const card = document.querySelector('.detail-wrapper');
  const thumbsWrapper = document.querySelector('.small-images-wrapper');
  const gallery = document.querySelector('.pdp-gallery');
  const reel = gallery?.querySelector('.pdp-gallery__reel');
  const stage = document.querySelector('.centered-wrapper');

  if (!card || !thumbsWrapper || !gallery || !reel || !stage) return;

  const thumbs = Array.from(thumbsWrapper.querySelectorAll('.img'));
  if (!thumbs.length) return;

  // Desktop reveals the reel on hover; touch layouts swap the stage on tap.
  const hoverQuery = window.matchMedia(
    window.PeakInput ? '(min-width: 992px)' : '(hover: hover) and (min-width: 992px)'
  );
  const canHover = () => hoverQuery.matches && (!window.PeakInput || window.PeakInput.canHover());

  // Set data-large on a thumbnail to point at proper artwork; otherwise the
  // thumbnail source is reused.
  const sourceFor = (thumb) =>
    thumb.dataset.large || thumb.querySelector('img')?.src || '';

  /* ---------- desktop: hover reel ---------- */

  let slides = [];
  let activeIndex = -1;
  let closeTimer = 0;

  function buildReel() {
    if (slides.length) return;

    slides = thumbs.map((thumb) => {
      const slide = document.createElement('div');
      slide.className = 'pdp-gallery__slide';

      const img = document.createElement('img');
      img.className = 'media-fluid';
      img.alt = '';
      img.fetchPriority = 'low';
      img.src = sourceFor(thumb);

      slide.appendChild(img);
      reel.appendChild(slide);
      return slide;
    });
  }

  /* --gallery-index is a float. The reel translates by it directly, so the
     pointer drives the strip continuously rather than jumping a whole slide
     at each thumbnail boundary. */
  function setActive(index) {
    const nearest = Math.round(index);

    if (nearest !== activeIndex) {
      slides[activeIndex]?.classList.remove('is-active');
      thumbs[activeIndex]?.classList.remove('is-active');
      slides[nearest]?.classList.add('is-active');
      thumbs[nearest]?.classList.add('is-active');
      activeIndex = nearest;
    }

    reel.style.setProperty('--gallery-index', index.toFixed(4));
  }

  /* ---------- pointer tracking ---------- */

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  // Pointer y across the thumbnail column maps onto the slide range.
  function indexFromPointer(clientY) {
    const r = thumbsWrapper.getBoundingClientRect();
    if (!r.height) return 0;
    const t = clamp((clientY - r.top) / r.height, 0, 1);
    return t * (thumbs.length - 1);
  }

  let targetIndex = 0;
  let shownIndex = 0;
  let followRaf = 0;
  let following = false;

  /* The hover marker: a rectangle larger than a tile that rides the column
     from pointer y. Driven by targetIndex (straight from the pointer), not by
     shownIndex (which lerps), so the marker stays glued to the cursor while
     the reel eases behind it. */
  const cursor = document.createElement('div');
  cursor.className = 'small-images-wrapper__cursor';
  cursor.setAttribute('aria-hidden', 'true');
  thumbsWrapper.appendChild(cursor);

  // Centre of the marker for a given fractional index, measured off the real
  // tiles so it stays correct if the tile size or gap changes.
  function cursorY(index) {
    const wrapRect = thumbsWrapper.getBoundingClientRect();
    const first = thumbs[0].getBoundingClientRect();
    const last = thumbs[thumbs.length - 1].getBoundingClientRect();
    const firstC = first.top + first.height / 2 - wrapRect.top;
    const lastC = last.top + last.height / 2 - wrapRect.top;
    const t = thumbs.length > 1 ? index / (thumbs.length - 1) : 0;
    return firstC + t * (lastC - firstC);
  }

  function placeCursor(index) {
    cursor.style.transform =
      'translate(-50%, -50%) translateY(' + cursorY(index).toFixed(2) + 'px)';
  }

  function follow() {
    // Light lerp: enough to smooth the pointer, not enough to feel like a snap.
    shownIndex += (targetIndex - shownIndex) * 0.22;

    if (Math.abs(targetIndex - shownIndex) < 0.001) shownIndex = targetIndex;
    setActive(shownIndex);

    if (following || shownIndex !== targetIndex) {
      followRaf = requestAnimationFrame(follow);
    } else {
      followRaf = 0;
    }
  }

  function startFollowing() {
    following = true;
    reel.classList.add('is-following');
    thumbsWrapper.classList.add('is-tracking');
    if (!followRaf) followRaf = requestAnimationFrame(follow);
  }

  function stopFollowing() {
    following = false;
    reel.classList.remove('is-following');
    thumbsWrapper.classList.remove('is-tracking');
  }

  function open(index) {
    clearTimeout(closeTimer);
    if (!canHover()) return;

    buildReel();

    // Opening presents the shot under the pointer already centred; after that
    // the reel is driven continuously by pointer y.
    const isOpening = !card.classList.contains('is-gallery-open');
    if (isOpening) {
      reel.classList.add('is-instant');
      shownIndex = index;
    }

    targetIndex = index;
    setActive(shownIndex);
    card.classList.add('is-gallery-open');

    if (isOpening) {
      requestAnimationFrame(() => reel.classList.remove('is-instant'));
    }

    startFollowing();
  }

  function close() {
    stopFollowing();
    card.classList.remove('is-gallery-open');
    thumbs[activeIndex]?.classList.remove('is-active');
  }

  function scheduleClose() {
    clearTimeout(closeTimer);
    closeTimer = setTimeout(close, 120);
  }

  /* ---------- touch: tap a thumbnail to fill the stage ---------- */

  const stagePhoto = document.createElement('img');
  stagePhoto.className = 'stage-photo';
  stagePhoto.alt = '';
  stage.appendChild(stagePhoto);

  let stageIndex = -1;

  function selectOnStage(index) {
    thumbs[stageIndex]?.classList.remove('is-active');

    // Tapping the current thumbnail again returns the stage to the bottle.
    if (index === stageIndex) {
      stage.classList.remove('is-photo-active');
      stageIndex = -1;
      return;
    }

    stagePhoto.src = sourceFor(thumbs[index]);
    stage.classList.add('is-photo-active');
    thumbs[index].classList.add('is-active');
    stageIndex = index;
  }

  /* ---------- wiring ---------- */

  thumbs.forEach((thumb, index) => {
    thumb.setAttribute('role', 'button');
    thumb.setAttribute('tabindex', '0');

    // No mouseenter here: hovering a single thumbnail would snap the reel.
    // Keyboard focus still jumps to a specific shot.
    thumb.addEventListener('focus', () => { placeCursor(index); open(index); });

    thumb.addEventListener('click', () => {
      if (canHover()) open(index);
      else selectOnStage(index);
    });

    thumb.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      thumb.click();
    });
  });

  // A short grace period stops the veil flickering when the pointer crosses
  // the gaps between thumbnails.
  thumbsWrapper.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'touch') return;
    clearTimeout(closeTimer);
    const i = indexFromPointer(event.clientY);
    placeCursor(i);
    open(i);
  });

  thumbsWrapper.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') return;
    if (!card.classList.contains('is-gallery-open')) return;
    targetIndex = indexFromPointer(event.clientY);
    placeCursor(targetIndex);
    startFollowing();
  });

  thumbsWrapper.addEventListener('mouseleave', scheduleClose);
  thumbsWrapper.addEventListener('focusout', (event) => {
    if (!thumbsWrapper.contains(event.relatedTarget)) scheduleClose();
  });

  const syncReel = () => {
    if (canHover()) buildReel();
    else close();
  };

  if (canHover()) buildReel();
  hoverQuery.addEventListener('change', syncReel);
  if (window.PeakInput) window.PeakInput.onChange(syncReel);
});
